import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bot, Send, Loader2 } from "lucide-react";
import type { SupabaseLint, SupabaseAdvisorType } from "@optidev/shared-types";
import { useAgentUIStore } from "@/stores/agentUIStore";

interface FixWithAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: () => void;
  lint: SupabaseLint | null;
  advisorType: SupabaseAdvisorType;
}

// Build the prompt that will be sent to the agent
export function buildAgentPrompt(lint: SupabaseLint, advisorType: SupabaseAdvisorType): string {
  const issueType = advisorType === "security" ? "security" : "performance";

  let prompt = `Fix this OptiDev Cloud ${issueType} issue:\n\n`;
  prompt += `**Issue:** ${lint.title}\n`;
  prompt += `**Level:** ${lint.level}\n`;

  if (lint.metadata && (lint.metadata.schema || lint.metadata.name)) {
    const affected = [
      lint.metadata.schema,
      lint.metadata.name,
    ].filter(Boolean).join(".");
    if (lint.metadata.type) {
      prompt += `**Affected:** ${affected} (${lint.metadata.type})\n`;
    } else {
      prompt += `**Affected:** ${affected}\n`;
    }
  }

  if (lint.description) {
    prompt += `\n**Description:**\n${lint.description}\n`;
  }

  if (lint.detail) {
    prompt += `\n**Details:**\n${lint.detail}\n`;
  }

  if (lint.remediation) {
    prompt += `\n**Remediation:**\n\`\`\`sql\n${lint.remediation}\n\`\`\`\n`;
  }

  return prompt;
}

export function FixWithAgentModal({
  isOpen,
  onClose,
  onSend,
  lint,
  advisorType,
}: FixWithAgentModalProps) {
  const isAgentStreaming = useAgentUIStore((state) => state.isAgentStreaming);

  if (!lint) return null;

  const prompt = buildAgentPrompt(lint, advisorType);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Fix with Agent
          </DialogTitle>
          <DialogDescription>
            This prompt will be sent to the Claude Agent to fix the issue.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto my-4">
          <div className="bg-muted rounded-lg p-4">
            <pre className="text-sm whitespace-pre-wrap font-mono">
              {prompt}
            </pre>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSend} disabled={isAgentStreaming}>
            {isAgentStreaming ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {isAgentStreaming ? "Agent Busy..." : "Send to Agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
